import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { ScheduleService } from 'src/app/shared/schedule.service';
import { SpinnerService } from 'src/app/shared/spinner.service';
import { MessageService } from 'src/app/shared/message.service';
import * as moment from 'moment';
import { PermissionsService } from 'src/app/shared/permissions.service';
import 'signalR';
import { Router, NavigationStart } from '@angular/router';
import { SignalRService } from 'src/app/shared/signal-r.service';
import { AppConfig } from 'src/app/app.config';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from 'src/app/shared/confirmation-dialog/confirmation-dialog.component';
import { AuthenticationService } from 'src/app/core/authentication/shared/authentication.service';
import { CanComponentDeactivate } from 'src/app/core/authentication/shared/pending-changes.guard';
import { map, take, debounceTime } from 'rxjs/operators';
import { timeout, finalize } from 'rxjs/operators'; 
import { Observable, Subject, Subscription } from 'rxjs';


/*
  IMPORTANT: Save/Autosave flow overview

  - Autosave (updateSchedule/commitUpdateSchedule) persists edits to a TEMPORARY schedule.
    It is debounced and guarded to avoid overlapping requests.

  - Save button (onSave) commits the temporary schedule to the PERSISTENT store on the server,
    then reloads the authoritative schedule via getSchedule and populates all fields/controls.

  - We pause autosave during Save (autosavePaused = true) to avoid blur-driven autosaves
    racing with the Save. Autosave resumes only after the authoritative getSchedule completes.

  - Validation blocking: if server returns validation errors, autosaves are blocked until
    a field within the implicated cards changes, preventing error-response loops and ensuring
    the user explicitly fixes issues before another server call.

  - Window status calls (isScheduleWindowOpen): after loading a schedule, we request each
    window's "is open" flag. This results in multiple HTTP calls (one per window card).
    Consider batching server-side in the future if you need to reduce network chatter.
*/

declare const $: any;
type JSONish = Record<string, unknown>;

@Component({
  selector: 'app-admin-schedule',
  templateUrl: './admin-schedule.component.html',
  styleUrls: ['./admin-schedule.component.css']
})
export class AdminScheduleComponent implements OnInit, CanComponentDeactivate {
  private config = AppConfig.settings;

  confirmationDialogRef: MatDialogRef<ConfirmationDialogComponent>;

  priceManagementHub;
  schedule: any = {};
  purgeOlderLogActionsDay = 'Day';

  // Panel visibility
  isWindowPanelOpen = true;
  isTaskPanelOpen = true;
  isOnDemandPanelOpen = true;

  // Per-window "open" booleans (populated via isScheduleWindowOpen calls)
  isServerMaintenanceWindowOpen;
  isPreviewPricingWindowOpen;
  isMeatPreviewPricingWindowOpen;
  isProducePreviewPricingWindowOpen;
  isStoreItemPriceChangesWindowOpen;
  isMeatStoreItemPriceChangesWindowOpen;
  isProduceStoreItemPriceChangesWindowOpen;

  hubsSubscription;
  routerSubscription;
  scheduleChangeSubscription;
  signalRTimeout;
  permissions: any = {};
  pricingRulesMessage = '';
  myUser;

  // suppressUpdate prevents programmatic server-side assignments from retriggering updateSchedule()
  private suppressUpdate: boolean = false;

  // Debounce support for updateSchedule()
  private updateSchedule$ = new Subject<void>();
  private updateScheduleSub: Subscription;
  private readonly updateDebounceMs = 250; // tweak as desired

  // Avoid overlapping/duplicate commits
  private inFlightUpdate: boolean = false;
  private pendingChange: boolean = false;
  private lastSentPayload: string = '';

  // New: pause autosave while committing via Save, or while authoritative reload is in progress
  private autosavePaused: boolean = false;
  // Helper to resume autosave after getSchedule() completes (post-save success)
  private resumeAutosaveAfterLoad: boolean = false;
  private reqSeq = 0; // simple sequence id for tracing

  // New: block server updates while validation errors are present
  private validationBlocked: boolean = false;
  private blockedGroups: Set<string> = new Set();
  private lastErrorScheduleSnapshot: any = null;

  // Add just below other private fields
  private changedKeys = new Set<string>();

  // Explicit mapping for rendered "Next" / TimeOfDayView fields -> underlying schedule field
  private renderedDateToBase: Record<string, string> = {
    serverMaintenanceWindowOpensNext: 'serverMaintenanceWindowOpens',
    serverMaintenanceWindowClosesNext: 'serverMaintenanceWindowCloses',
    previewPricingWindowOpensNext: 'previewPricingWindowOpens',
    previewPricingWindowClosesNext: 'previewPricingWindowCloses',
    meatPreviewPricingWindowOpensNext: 'meatPreviewPricingWindowOpens',
    meatPreviewPricingWindowClosesNext: 'meatPreviewPricingWindowCloses',
    producePreviewPricingWindowOpensNext: 'producePreviewPricingWindowOpens',
    producePreviewPricingWindowClosesNext: 'producePreviewPricingWindowCloses',
    storeItemPriceChangesWindowOpensNext: 'storeItemPriceChangesWindowOpens',
    storeItemPriceChangesWindowClosesNext: 'storeItemPriceChangesWindowCloses',
    meatStoreItemPriceChangesWindowOpensNext: 'meatStoreItemPriceChangesWindowOpens',
    meatStoreItemPriceChangesWindowClosesNext: 'meatStoreItemPriceChangesWindowCloses',
    produceStoreItemPriceChangesWindowOpensNext: 'produceStoreItemPriceChangesWindowOpens',
    produceStoreItemPriceChangesWindowClosesNext: 'produceStoreItemPriceChangesWindowCloses',
    nowChangesLockoutWindowOpensNext: 'nowChangesLockoutWindowOpens',
    nowChangesLockoutWindowClosesNext: 'nowChangesLockoutWindowCloses',
    purgeOldLogActionsNext: 'purgeOldLogActions',
    purgeOldMessagesNext: 'purgeOldMessages',
    purgeOldAdItemsNext: 'purgeOldAdItems',
    purgeOldStoreItemPriceChangesNext: 'purgeOldStoreItemPriceChanges',
    purgeOldStoreItemCostChangesNext: 'purgeOldStoreItemCostChanges',
    purgeOldDenormalizedPreviewStoreItemPriceChangesNext: 'purgeOldDenormalizedPreviewStoreItemPriceChanges',
    updateStoreItemsFromPreviewStoreItemsNext: 'updateStoreItemsFromPreviewStoreItems',
    updateStoreItemsFromMeatAndProducePreviewStoreItemsNext: 'updateStoreItemsFromMeatAndProducePreviewStoreItems',
    deleteMeatAndProducePreviewStoreItemsNext: 'deleteMeatAndProducePreviewStoreItems',
    incrementStoreItemsWeeksInStoreNext: 'incrementStoreItemsWeeksInStore',
    resetStoreItemsMovementAndWeeksInStoreNext: 'resetStoreItemsMovementAndWeeksInStore'
  };

  links = [
    'All',
    'Daily',
    'Weekly',
    'Yearly'
  ]

  activeLink = this.links[0];

  daysOfWeek = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'
  ]

  hasChanged: boolean = false;
  hasErrors: boolean = false;
  // Added: Save button in-flight guard (do not remove existing comments)
  isSaving: boolean = false;
  originalValues: JSONish = {};

  // Maps server validation keys to UI card groups for highlighting
  errorMessageMap = {
    "ServerLocationTimeZone": { "group": "ServerLocationTimeZone", "field": "ServerLocationTimeZone" },
    "ServerMaintenanceWindowOpens": { "group": "ServerMaintenanceWindow", "field": "ServerMaintenanceWindowOpens" },
    "ServerMaintenanceWindowOpensDayOfWeek": { "group": "ServerMaintenanceWindow", "field": "ServerMaintenanceWindowOpensDayOfWeek" },
    "ServerMaintenanceWindowOpensTimeOfDay": { "group": "ServerMaintenanceWindow", "field": "ServerMaintenanceWindowOpensTimeOfDay" },
    "ServerMaintenanceWindowCloses": { "group": "ServerMaintenanceWindow", "field": "ServerMaintenanceWindowCloses" },
    "ServerMaintenanceWindowClosesDayOfWeek": { "group": "ServerMaintenanceWindow", "field": "ServerMaintenanceWindowClosesDayOfWeek" },
    "ServerMaintenanceWindowClosesTimeOfDay": { "group": "ServerMaintenanceWindow", "field": "ServerMaintenanceWindowClosesTimeOfDay" },
    "PreviewPricingWindowOpens": { "group": "PreviewPricingWindow", "field": "PreviewPricingWindowOpens" },
    "PreviewPricingWindowOpensDayOfWeek": { "group": "PreviewPricingWindow", "field": "PreviewPricingWindowOpensDayOfWeek" },
    "PreviewPricingWindowOpensTimeOfDay": { "group": "PreviewPricingWindow", "field": "PreviewPricingWindowOpensTimeOfDay" },
    "PreviewPricingWindowCloses": { "group": "PreviewPricingWindow", "field": "PreviewPricingWindowCloses" },
    "PreviewPricingWindowClosesDayOfWeek": { "group": "PreviewPricingWindow", "field": "PreviewPricingWindowClosesDayOfWeek" },
    "PreviewPricingWindowClosesTimeOfDay": { "group": "PreviewPricingWindow", "field": "PreviewPricingWindowClosesTimeOfDay" },
    "MeatPreviewPricingWindowOpens": { "group": "MeatPreviewPricingWindow", "field": "MeatPreviewPricingWindowOpens" },
    "MeatPreviewPricingWindowOpensDayOfWeek": { "group": "MeatPreviewPricingWindow", "field": "MeatPreviewPricingWindowOpensDayOfWeek" },
    "MeatPreviewPricingWindowOpensTimeOfDay": { "group": "MeatPreviewPricingWindow", "field": "MeatPreviewPricingWindowOpensTimeOfDay" },
    "MeatPreviewPricingWindowCloses": { "group": "MeatPreviewPricingWindow", "field": "MeatPreviewPricingWindowCloses" },
    "MeatPreviewPricingWindowClosesDayOfWeek": { "group": "MeatPreviewPricingWindow", "field": "MeatPreviewPricingWindowClosesDayOfWeek" },
    "MeatPreviewPricingWindowClosesTimeOfDay": { "group": "MeatPreviewPricingWindow", "field": "MeatPreviewPricingWindowClosesTimeOfDay" },
    "ProducePreviewPricingWindowOpens": { "group": "ProducePreviewPricingWindow", "field": "ProducePreviewPricingWindowOpens" },
    "ProducePreviewPricingWindowOpensDayOfWeek": { "group": "ProducePreviewPricingWindow", "field": "ProducePreviewPricingWindowOpensDayOfWeek" },
    "ProducePreviewPricingWindowOpensTimeOfDay": { "group": "ProducePreviewPricingWindow", "field": "ProducePreviewPricingWindowOpensTimeOfDay" },
    "ProducePreviewPricingWindowCloses": { "group": "ProducePreviewPricingWindow", "field": "ProducePreviewPricingWindowCloses" },
    "ProducePreviewPricingWindowClosesDayOfWeek": { "group": "ProducePreviewPricingWindow", "field": "ProducePreviewPricingWindowClosesDayOfWeek" },
    "ProducePreviewPricingWindowClosesTimeOfDay": { "group": "ProducePreviewPricingWindow", "field": "ProducePreviewPricingWindowClosesTimeOfDay" },
    "StoreItemPriceChangesWindowOpens": { "group": "StoreItemPriceChangesWindow", "field": "StoreItemPriceChangesWindowOpens" },
    "StoreItemPriceChangesWindowOpensDayOfWeek": { "group": "StoreItemPriceChangesWindow", "field": "StoreItemPriceChangesWindowOpensDayOfWeek" },
    "StoreItemPriceChangesWindowOpensTimeOfDay": { "group": "StoreItemPriceChangesWindow", "field": "StoreItemPriceChangesWindowOpensTimeOfDay" },
    "StoreItemPriceChangesWindowCloses": { "group": "StoreItemPriceChangesWindow", "field": "StoreItemPriceChangesWindowCloses" },
    // Edited: fixed field key to ensure error grouping works (was "...&ClosesDayOfWeek")
    "StoreItemPriceChangesWindowClosesDayOfWeek": { "group": "StoreItemPriceChangesWindow", "field": "StoreItemPriceChangesWindowClosesDayOfWeek" },
    "StoreItemPriceChangesWindowClosesTimeOfDay": { "group": "StoreItemPriceChangesWindow", "field": "StoreItemPriceChangesWindowClosesTimeOfDay" },
    "MeatStoreItemPriceChangesWindowOpens": { "group": "MeatStoreItemPriceChangesWindow", "field": "MeatStoreItemPriceChangesWindowOpens" },
    "MeatStoreItemPriceChangesWindowOpensDayOfWeek": { "group": "MeatStoreItemPriceChangesWindow", "field": "MeatStoreItemPriceChangesWindowOpensDayOfWeek" },
    "MeatStoreItemPriceChangesWindowOpensTimeOfDay": { "group": "MeatStoreItemPriceChangesWindow", "field": "MeatStoreItemPriceChangesWindowOpensTimeOfDay" },
    "MeatStoreItemPriceChangesWindowCloses": { "group": "MeatStoreItemPriceChangesWindow", "field": "MeatStoreItemPriceChangesWindowCloses" },
    "MeatStoreItemPriceChangesWindowClosesDayOfWeek": { "group": "MeatStoreItemPriceChangesWindow", "field": "MeatStoreItemPriceChangesWindowClosesDayOfWeek" },
    "MeatStoreItemPriceChangesWindowClosesTimeOfDay": { "group": "MeatStoreItemPriceChangesWindow", "field": "MeatStoreItemPriceChangesWindowClosesTimeOfDay" },
    "ProduceStoreItemPriceChangesWindowOpens": { "group": "ProduceStoreItemPriceChangesWindow", "field": "ProduceStoreItemPriceChangesWindowOpens" },
    "ProduceStoreItemPriceChangesWindowOpensDayOfWeek": { "group": "ProduceStoreItemPriceChangesWindow", "field": "ProduceStoreItemPriceChangesWindowOpensDayOfWeek" },
    "ProduceStoreItemPriceChangesWindowOpensTimeOfDay": { "group": "ProduceStoreItemPriceChangesWindow", "field": "ProduceStoreItemPriceChangesWindowOpensTimeOfDay" },
    "ProduceStoreItemPriceChangesWindowCloses": { "group": "ProduceStoreItemPriceChangesWindow", "field": "ProduceStoreItemPriceChangesWindowCloses" },
    "ProduceStoreItemPriceChangesWindowClosesDayOfWeek": { "group": "ProduceStoreItemPriceChangesWindow", "field": "ProduceStoreItemPriceChangesWindowClosesDayOfWeek" },
    "ProduceStoreItemPriceChangesWindowClosesTimeOfDay": { "group": "ProduceStoreItemPriceChangesWindow", "field": "ProduceStoreItemPriceChangesWindowClosesTimeOfDay" },
    "NowChangesLockoutWindowOpens": { "group": "NowChangesLockoutWindow", "field": "NowChangesLockoutWindowOpens" },
    "NowChangesLockoutWindowOpensDayOfWeek": { "group": "NowChangesLockoutWindow", "field": "NowChangesLockoutWindowOpensDayOfWeek" },
    "NowChangesLockoutWindowOpensTimeOfDay": { "group": "NowChangesLockoutWindow", "field": "NowChangesLockoutWindowOpensTimeOfDay" },
    "NowChangesLockoutWindowCloses": { "group": "NowChangesLockoutWindow", "field": "NowChangesLockoutWindowCloses" },
    "NowChangesLockoutWindowClosesDayOfWeek": { "group": "NowChangesLockoutWindow", "field": "NowChangesLockoutWindowClosesDayOfWeek" },
    "NowChangesLockoutWindowClosesTimeOfDay": { "group": "NowChangesLockoutWindow", "field": "NowChangesLockoutWindowClosesTimeOfDay" },
    "UpdateStoreItemsFromPreviewStoreItems": { "group": "UpdateStoreItemsFromPreviewStoreItems", "field": "UpdateStoreItemsFromPreviewStoreItems" },
    "UpdateStoreItemsFromPreviewStoreItemsDayOfWeek": { "group": "UpdateStoreItemsFromPreviewStoreItems", "field": "UpdateStoreItemsFromPreviewStoreItemsDayOfWeek" },
    "UpdateStoreItemsFromPreviewStoreItemsTimeOfDay": { "group": "UpdateStoreItemsFromPreviewStoreItems", "field": "UpdateStoreItemsFromPreviewStoreItemsTimeOfDay" },
    "UpdateStoreItemsFromPreviewStoreItemsExecutionRequested": { "group": "UpdateStoreItemsFromPreviewStoreItems", "field": "UpdateStoreItemsFromPreviewStoreItemsExecutionRequested" },
    "UpdateStoreItemsFromPreviewStoreItemsLastStarted": { "group": "UpdateStoreItemsFromPreviewStoreItems", "field": "UpdateStoreItemsFromPreviewStoreItemsLastStarted" },
    "UpdateStoreItemsFromPreviewStoreItemsLastCompleted": { "group": "UpdateStoreItemsFromPreviewStoreItems", "field": "UpdateStoreItemsFromPreviewStoreItemsLastCompleted" },
    "UpdateStoreItemsFromPreviewStoreItemsLastExecutionSuccessful": { "group": "UpdateStoreItemsFromPreviewStoreItems", "field": "UpdateStoreItemsFromPreviewStoreItemsLastExecutionSuccessful" },

    "UpdateStoreItemsFromMeatAndProducePreviewStoreItems": { "group": "UpdateStoreItemsFromMeatAndProducePreviewStoreItems", "field": "UpdateStoreItemsFromMeatAndProducePreviewStoreItems" },
    "UpdateStoreItemsFromMeatAndProducePreviewStoreItemsDayOfWeek": { "group": "UpdateStoreItemsFromMeatAndProducePreviewStoreItems", "field": "UpdateStoreItemsFromMeatAndProducePreviewStoreItemsDayOfWeek" },
    "UpdateStoreItemsFromMeatAndProducePreviewStoreItemsTimeOfDay": { "group": "UpdateStoreItemsFromMeatAndProducePreviewStoreItems", "field": "UpdateStoreItemsFromMeatAndProducePreviewStoreItemsTimeOfDay" },
    "UpdateStoreItemsFromMeatAndProducePreviewStoreItemsExecutionRequested": { "group": "UpdateStoreItemsFromMeatAndProducePreviewStoreItems", "field": "UpdateStoreItemsFromMeatAndProducePreviewStoreItemsExecutionRequested" },
    "UpdateStoreItemsFromMeatAndProducePreviewStoreItemsLastStarted": { "group": "UpdateStoreItemsFromMeatAndProducePreviewStoreItems", "field": "UpdateStoreItemsFromMeatAndProducePreviewStoreItemsLastStarted" },
    "UpdateStoreItemsFromMeatAndProducePreviewStoreItemsLastCompleted": { "group": "UpdateStoreItemsFromMeatAndProducePreviewStoreItems", "field": "UpdateStoreItemsFromMeatAndProducePreviewStoreItemsLastCompleted" },
    "UpdateStoreItemsFromMeatAndProducePreviewStoreItemsLastExecutionSuccessful": { "group": "UpdateStoreItemsFromMeatAndProducePreviewStoreItems", "field": "UpdateStoreItemsFromMeatAndProducePreviewStoreItemsLastExecutionSuccessful" },

    "DeleteMeatAndProducePreviewStoreItems": { "group": "DeleteMeatAndProducePreviewStoreItems", "field": "DeleteMeatAndProducePreviewStoreItems" },
    "DeleteMeatAndProducePreviewStoreItemsDayOfWeek": { "group": "DeleteMeatAndProducePreviewStoreItems", "field": "DeleteMeatAndProducePreviewStoreItemsDayOfWeek" },
    "DeleteMeatAndProducePreviewStoreItemsTimeOfDay": { "group": "DeleteMeatAndProducePreviewStoreItems", "field": "DeleteMeatAndProducePreviewStoreItemsTimeOfDay" },
    "DeleteMeatAndProducePreviewStoreItemsExecutionRequested": { "group": "DeleteMeatAndProducePreviewStoreItems", "field": "DeleteMeatAndProducePreviewStoreItemsExecutionRequested" },
    "DeleteMeatAndProducePreviewStoreItemsLastStarted": { "group": "DeleteMeatAndProducePreviewStoreItems", "field": "DeleteMeatAndProducePreviewStoreItemsLastStarted" },
    "DeleteMeatAndProducePreviewStoreItemsLastCompleted": { "group": "DeleteMeatAndProducePreviewStoreItems", "field": "DeleteMeatAndProducePreviewStoreItemsLastCompleted" },
    "DeleteMeatAndProducePreviewStoreItemsLastExecutionSuccessful": { "group": "DeleteMeatAndProducePreviewStoreItems", "field": "DeleteMeatAndProducePreviewStoreItemsLastExecutionSuccessful" },

    "IncrementStoreItemsWeeksInStore": { "group": "IncrementStoreItemsWeeksInStore", "field": "IncrementStoreItemsWeeksInStore" },
    "IncrementStoreItemsWeeksInStoreDayOfWeek": { "group": "IncrementStoreItemsWeeksInStore", "field": "IncrementStoreItemsWeeksInStoreDayOfWeek" },
    "IncrementStoreItemsWeeksInStoreTimeOfDay": { "group": "IncrementStoreItemsWeeksInStore", "field": "IncrementStoreItemsWeeksInStoreTimeOfDay" },
    "IncrementStoreItemsWeeksInStoreExecutionRequested": { "group": "IncrementStoreItemsWeeksInStore", "field": "IncrementStoreItemsWeeksInStoreExecutionRequested" },
    "IncrementStoreItemsWeeksInStoreLastStarted": { "group": "IncrementStoreItemsWeeksInStore", "field": "IncrementStoreItemsWeeksInStoreLastStarted" },
    "IncrementStoreItemsWeeksInStoreLastCompleted": { "group": "IncrementStoreItemsWeeksInStore", "field": "IncrementStoreItemsWeeksInStoreLastCompleted" },
    "IncrementStoreItemsWeeksInStoreLastExecutionSuccessful": { "group": "IncrementStoreItemsWeeksInStore", "field": "IncrementStoreItemsWeeksInStoreLastExecutionSuccessful" },

    "ResetStoreItemsMovementAndWeeksInStore": { "group": "ResetStoreItemsMovementAndWeeksInStore", "field": "ResetStoreItemsMovementAndWeeksInStore" },
    "ResetStoreItemsMovementAndWeeksInStoreDayOfWeek": { "group": "ResetStoreItemsMovementAndWeeksInStore", "field": "ResetStoreItemsMovementAndWeeksInStoreDayOfWeek" },
    "ResetStoreItemsMovementAndWeeksInStoreTimeOfDay": { "group": "ResetStoreItemsMovementAndWeeksInStore", "field": "ResetStoreItemsMovementAndWeeksInStoreTimeOfDay" },
    "ResetStoreItemsMovementAndWeeksInStoreExecutionRequested": { "group": "ResetStoreItemsMovementAndWeeksInStore", "field": "ResetStoreItemsMovementAndWeeksInStoreExecutionRequested" },
    "ResetStoreItemsMovementAndWeeksInStoreLastStarted": { "group": "ResetStoreItemsMovementAndWeeksInStore", "field": "ResetStoreItemsMovementAndWeeksInStoreLastStarted" },
    "ResetStoreItemsMovementAndWeeksInStoreLastCompleted": { "group": "ResetStoreItemsMovementAndWeeksInStore", "field": "ResetStoreItemsMovementAndWeeksInStoreLastCompleted" },
    "ResetStoreItemsMovementAndWeeksInStoreLastExecutionSuccessful": { "group": "ResetStoreItemsMovementAndWeeksInStore", "field": "ResetStoreItemsMovementAndWeeksInStoreLastExecutionSuccessful" },

    "LogActionsTimeToLiveInDays": { "group": "LogActionsTimeToLiveInDays", "field": "LogActionsTimeToLiveInDays" },
    "PurgeOldLogActions": { "group": "PurgeOldLogActions", "field": "PurgeOldLogActions" },
    "PurgeOldLogActionsDayOfWeek": { "group": "PurgeOldLogActions", "field": "PurgeOldLogActionsDayOfWeek" },
    "PurgeOldLogActionsTimeOfDay": { "group": "PurgeOldLogActions", "field": "PurgeOldLogActionsTimeOfDay" },
    "PurgeOldLogActionsExecutionRequested": { "group": "PurgeOldLogActions", "field": "PurgeOldLogActionsExecutionRequested" },
    "PurgeOldLogActionsLastStarted": { "group": "PurgeOldLogActions", "field": "PurgeOldLogActionsLastStarted" },
    "PurgeOldLogActionsLastCompleted": { "group": "PurgeOldLogActions", "field": "PurgeOldLogActionsLastCompleted" },
    "PurgeOldLogActionsLastExecutionSuccessful": { "group": "PurgeOldLogActions", "field": "PurgeOldLogActionsLastExecutionSuccessful" },

    "MessagesTimeToLiveInDays": { "group": "MessagesTimeToLiveInDays", "field": "MessagesTimeToLiveInDays" },
    "PurgeOldMessages": { "group": "PurgeOldMessages", "field": "PurgeOldMessages" },
    "PurgeOldMessagesDayOfWeek": { "group": "PurgeOldMessages", "field": "PurgeOldMessagesDayOfWeek" },
    "PurgeOldMessagesTimeOfDay": { "group": "PurgeOldMessages", "field": "PurgeOldMessagesTimeOfDay" },
    "PurgeOldMessagesExecutionRequested": { "group": "PurgeOldMessages", "field": "PurgeOldMessagesExecutionRequested" },
    "PurgeOldMessagesLastStarted": { "group": "PurgeOldMessages", "field": "PurgeOldMessagesLastStarted" },
    "PurgeOldMessagesLastCompleted": { "group": "PurgeOldMessages", "field": "PurgeOldMessagesLastCompleted" },
    "PurgeOldMessagesLastExecutionSuccessful": { "group": "PurgeOldMessages", "field": "PurgeOldMessagesLastExecutionSuccessful" },

    "AdItemsTimeToLiveInWeeks": { "group": "AdItemsTimeToLiveInWeeks", "field": "AdItemsTimeToLiveInWeeks" },
    "PurgeOldAdItems": { "group": "PurgeOldAdItems", "field": "PurgeOldAdItems" },
    "PurgeOldAdItemsDayOfWeek": { "group": "PurgeOldAdItems", "field": "PurgeOldAdItemsDayOfWeek" },
    "PurgeOldAdItemsTimeOfDay": { "group": "PurgeOldAdItems", "field": "PurgeOldAdItemsTimeOfDay" },
    "PurgeOldAdItemsExecutionRequested": { "group": "PurgeOldAdItems", "field": "PurgeOldAdItemsExecutionRequested" },
    "PurgeOldAdItemsLastStarted": { "group": "PurgeOldAdItems", "field": "PurgeOldAdItemsLastStarted" },
    "PurgeOldAdItemsLastCompleted": { "group": "PurgeOldAdItems", "field": "PurgeOldAdItemsLastCompleted" },
    "PurgeOldAdItemsLastExecutionSuccessful": { "group": "PurgeOldAdItems", "field": "PurgeOldAdItemsLastExecutionSuccessful" },

    "StoreItemPriceChangesTimeToLiveInWeeks": { "group": "StoreItemPriceChangesTimeToLiveInWeeks", "field": "StoreItemPriceChangesTimeToLiveInWeeks" },
    "PurgeOldStoreItemPriceChanges": { "group": "PurgeOldStoreItemPriceChanges", "field": "PurgeOldStoreItemPriceChanges" },
    "PurgeOldStoreItemPriceChangesDayOfWeek": { "group": "PurgeOldStoreItemPriceChanges", "field": "PurgeOldStoreItemPriceChangesDayOfWeek" },
    "PurgeOldStoreItemPriceChangesTimeOfDay": { "group": "PurgeOldStoreItemPriceChanges", "field": "PurgeOldStoreItemPriceChangesTimeOfDay" },
    "PurgeOldStoreItemPriceChangesExecutionRequested": { "group": "PurgeOldStoreItemPriceChanges", "field": "PurgeOldStoreItemPriceChangesExecutionRequested" },
    "PurgeOldStoreItemPriceChangesLastStarted": { "group": "PurgeOldStoreItemPriceChanges", "field": "PurgeOldStoreItemPriceChangesLastStarted" },
    "PurgeOldStoreItemPriceChangesLastCompleted": { "group": "PurgeOldStoreItemPriceChanges", "field": "PurgeOldStoreItemPriceChangesLastCompleted" },
    "PurgeOldStoreItemPriceChangesLastExecutionSuccessful": { "group": "PurgeOldStoreItemPriceChanges", "field": "PurgeOldStoreItemPriceChangesLastExecutionSuccessful" },

    "StoreItemCostChangesTimeToLiveInWeeks": { "group": "StoreItemCostChangesTimeToLiveInWeeks", "field": "StoreItemCostChangesTimeToLiveInWeeks" },
    "PurgeOldStoreItemCostChanges": { "group": "PurgeOldStoreItemCostChanges", "field": "PurgeOldStoreItemCostChanges" },
    "PurgeOldStoreItemCostChangesDayOfWeek": { "group": "PurgeOldStoreItemCostChanges", "field": "PurgeOldStoreItemCostChangesDayOfWeek" },
    "PurgeOldStoreItemCostChangesTimeOfDay": { "group": "PurgeOldStoreItemCostChanges", "field": "PurgeOldStoreItemCostChangesTimeOfDay" },
    "PurgeOldStoreItemCostChangesExecutionRequested": { "group": "PurgeOldStoreItemCostChanges", "field": "PurgeOldStoreItemCostChangesExecutionRequested" },
    "PurgeOldStoreItemCostChangesLastStarted": { "group": "PurgeOldStoreItemCostChanges", "field": "PurgeOldStoreItemCostChangesLastStarted" },
    "PurgeOldStoreItemCostChangesLastCompleted": { "group": "PurgeOldStoreItemCostChanges", "field": "PurgeOldStoreItemCostChangesLastCompleted" },
    "PurgeOldStoreItemCostChangesLastExecutionSuccessful": { "group": "PurgeOldStoreItemCostChanges", "field": "PurgeOldStoreItemCostChangesLastExecutionSuccessful" },

    "DenormalizedPreviewStoreItemPriceChangesTimeToLiveInWeeks": { "group": "DenormalizedPreviewStoreItemPriceChangesTimeToLiveInWeeks", "field": "DenormalizedPreviewStoreItemPriceChangesTimeToLiveInWeeks" },
    "PurgeOldDenormalizedPreviewStoreItemPriceChanges": { "group": "PurgeOldDenormalizedPreviewStoreItemPriceChanges", "field": "PurgeOldDenormalizedPreviewStoreItemPriceChanges" },
    "PurgeOldDenormalizedPreviewStoreItemPriceChangesDayOfWeek": { "group": "PurgeOldDenormalizedPreviewStoreItemPriceChanges", "field": "PurgeOldDenormalizedPreviewStoreItemPriceChangesDayOfWeek" },
    "PurgeOldDenormalizedPreviewStoreItemPriceChangesTimeOfDay": { "group": "PurgeOldDenormalizedPreviewStoreItemPriceChanges", "field": "PurgeOldDenormalizedPreviewStoreItemPriceChangesTimeOfDay" },
    "PurgeOldDenormalizedPreviewStoreItemPriceChangesExecutionRequested": { "group": "PurgeOldDenormalizedPreviewStoreItemPriceChanges", "field": "PurgeOldDenormalizedPreviewStoreItemPriceChangesExecutionRequested" },
    "PurgeOldDenormalizedPreviewStoreItemPriceChangesLastStarted": { "group": "PurgeOldDenormalizedPreviewStoreItemPriceChanges", "field": "PurgeOldDenormalizedPreviewStoreItemPriceChangesLastStarted" },
    "PurgeOldDenormalizedPreviewStoreItemPriceChangesLastCompleted": { "group": "PurgeOldDenormalizedPreviewStoreItemPriceChanges", "field": "PurgeOldDenormalizedPreviewStoreItemPriceChangesLastCompleted" },
    "PurgeOldDenormalizedPreviewStoreItemPriceChangesLastExecutionSuccessful": { "group": "PurgeOldDenormalizedPreviewStoreItemPriceChanges", "field": "PurgeOldDenormalizedPreviewStoreItemPriceChangesLastExecutionSuccessful" },

    "ProcessPricingRules": { "group": "ProcessPricingRules", "field": "ProcessPricingRules" },
    "ProcessPricingRulesDayOfWeek": { "group": "ProcessPricingRules", "field": "ProcessPricingRulesDayOfWeek" },
    "ProcessPricingRulesTimeOfDay": { "group": "ProcessPricingRules", "field": "ProcessPricingRulesTimeOfDay" },
    "ProcessPricingRulesExecutionRequested": { "group": "ProcessPricingRules", "field": "ProcessPricingRulesExecutionRequested" },
    "ProcessPricingRulesLastStarted": { "group": "ProcessPricingRules", "field": "ProcessPricingRulesLastStarted" },
    "ProcessPricingRulesLastCompleted": { "group": "ProcessPricingRules", "field": "ProcessPricingRulesLastCompleted" },
    "ProcessPricingRulesLastExecutionSuccessful": { "group": "ProcessPricingRules", "field": "ProcessPricingRulesLastExecutionSuccessful" }
  }

  errors: any = {};

  // Map error group names to element IDs for scrolling
  private errorGroupToElementId: Record<string, string> = {
    'ServerMaintenanceWindow': 'card-server-maintenance',
    'PreviewPricingWindow': 'card-preview-pricing',
    'MeatPreviewPricingWindow': 'card-meat-preview-pricing',
    'ProducePreviewPricingWindow': 'card-produce-preview-pricing',
    'StoreItemPriceChangesWindow': 'card-store-item-price-changes',
    'MeatStoreItemPriceChangesWindow': 'card-meat-store-item-price-changes',
    'ProduceStoreItemPriceChangesWindow': 'card-produce-store-item-price-changes',
    'NowChangesLockoutWindow': 'card-now-changes-lockout',
    'UpdateStoreItemsFromPreviewStoreItems': 'card-update-store-items-from-preview',
    'UpdateStoreItemsFromMeatAndProducePreviewStoreItems': 'card-update-store-items-from-meat-produce-preview',
    'DeleteMeatAndProducePreviewStoreItems': 'card-delete-meat-produce-preview',
    'IncrementStoreItemsWeeksInStore': 'card-increment-weeks-in-store',
    'ResetStoreItemsMovementAndWeeksInStore': 'card-reset-movement-weeks',
    'PurgeOldLogActions': 'card-purge-log-actions',
    'PurgeOldMessages': 'card-purge-messages',
    'PurgeOldAdItems': 'card-purge-ad-items',
    'PurgeOldStoreItemPriceChanges': 'card-purge-store-item-price-changes',
    'PurgeOldStoreItemCostChanges': 'card-purge-store-item-cost-changes',
    'PurgeOldDenormalizedPreviewStoreItemPriceChanges': 'card-purge-denormalized-preview',
    'ProcessPricingRules': 'card-process-pricing-rules',
    'General': 'card-general-errors'
  };

  constructor(
    private _scheduleService: ScheduleService,
    private _spinnerService: SpinnerService,
    private _authenticationService: AuthenticationService,
    private _signalRService: SignalRService,
    private _messageService: MessageService,
    private _dialog: MatDialog,
    private _permissionsService: PermissionsService,
    private _router: Router,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    if (this.config.logInits) console.log('schedule init');
    this.permissions = this._permissionsService.getPermissions();
    this.myUser = this._authenticationService.getMyUser();

    // subscribe debounced update handler
    this.updateScheduleSub = this.updateSchedule$.pipe(
      debounceTime(this.updateDebounceMs)
    ).subscribe(() => this.commitUpdateSchedule());

    // Initial authoritative load of the persistent schedule
    this.getSchedule();
  }

  canDeactivate(): boolean | Observable<boolean> {
    if (!this.hasChanged) {
      return true; // allow navigation immediately
    }

    const dialogRef = this._dialog.open(ConfirmationDialogComponent, {
      disableClose: false,
      data: {
        title: 'Are you sure?',
        message: 'You have unsaved changes. Do you really want to leave?',
        confirmText: 'Yes, leave and disregard my changes',
        cancelText: 'No, cancel this action!'
      }
    });

    // Return the observable; the router waits for it
    return dialogRef.afterClosed().pipe(
      take(1),
      map(result => !!result) // truthy = proceed, falsy = stay
    );
  }

  isChanged(key: string): boolean {
    return !AdminScheduleComponent.deepEqual(this.schedule?.[key], this.originalValues?.[key]);
  }

  ngAfterContentInit() {
    var self = this;
    // subscribe to signalR hubs loading
    // this.hubsSubscription = this._notificationWindowService.areHubsLoaded$
    // .subscribe(
    //   (data) => {
    //     if (data) self.initSignalR();
    //   }
    // );

    // subscribe to route changes
    // https://stackoverflow.com/questions/34444822/angular2-watch-for-route-change
    this.routerSubscription = this._router.events.subscribe((event: any) => {
      //console.log(event);
      if (event instanceof NavigationStart) {
        //console.log(event.url);
        //$.connection.hub.stop();
      }
    });

    // Real-time: if server broadcasts a schedule change (external or background),
    // reload the latest authoritative sPress control altchedule so the UI stays in sync.
    this.scheduleChangeSubscription = this._signalRService.scheduleChange$
      .subscribe(
        (data) => {
          if (data) self.getSchedule();
        }
      );
  }

  ngOnDestroy() {
    //this.hubsSubscription.unsubscribe();
    this.routerSubscription.unsubscribe();
    this.scheduleChangeSubscription.unsubscribe();
    clearTimeout(this.signalRTimeout);

    // unsubscribe debounced subject
    if (this.updateScheduleSub) this.updateScheduleSub.unsubscribe();
  }

  // Authoritative schedule fetch. This is called:
  //  - on initial load
  //  - after a successful Save (commitTemporarySchedule)
  // It populates all fields/controls from the persistent schedule and resets dirty tracking.
  getSchedule() {
    this.errors = {}; // reset errors object
    this.hasErrors = false;
    this._scheduleService.getSchedule()
      .subscribe(
        (data) => {
          // prevent programmatic assignment from firing updateSchedule handlers
          this.suppressUpdate = true;
          this.schedule = data;

          // single batched refresh (fallbacks to per-window if not available)
          this.refreshWindowOpenStatesBatch();

          this.formatSchedule();

          // RESET dirty tracking AFTER authoritative schedule is applied,
          // so pending-changes guard reflects the server-committed state.
          this.resetDirtyTracking();

          // Resume autosave after reload if Save initiated this fetch
          if (this.resumeAutosaveAfterLoad) {
            this.autosavePaused = false;
            this.resumeAutosaveAfterLoad = false;
            // clear dedupe so next user change isn't dropped
            this.lastSentPayload = '';
          }

          // Clear save guard once authoritative schedule loaded (added)
          if (this.isSaving) this.isSaving = false;

          this.suppressUpdate = false;
        },
        (response) => {
          this._messageService.onFailure('Failed to get schedule.', response);
          // Ensure save guard released on failure (added)
          this.isSaving = false;
        }
      )
  }

  getTemporarySchedule() {
    this.errors = {}; // reset errors object
    this.hasErrors = false;
    this._scheduleService.getTemporarySchedule()
      .subscribe(
        (data: any) => {
          // server returns { ValidationResult, Schedule } - ensure we pick the inner Schedule
          const payload = data || {};

          // prevent programmatic assignment from firing updateSchedule handlers
          this.suppressUpdate = true;

          this.schedule = payload.schedule ? payload.schedule : payload;

          // If validation came back and failed, set hasErrors so the UI can react
          if (payload.validationResult && payload.validationResult.Succeeded === false) {
            this.hasErrors = true;
            // optionally surface errors -- server's identity errors may be in payload.ValidationResult.Errors
            this._messageService.showErrorToastTopRight('Temporary schedule has validation issues.');
          }

          // Refresh window open states after loading temporary schedule
          this.refreshWindowOpenStatesBatch();

          this.formatSchedule();

          this.suppressUpdate = false;
        },
        (response) => {
          this._messageService.onFailure('Failed to get schedule.', response);
        }
      )
  }


  // helper: apply server payload (ScheduleWithValidationReturnModel) to UI and annotate errors
  private applySchedulePayload(payload: any) {
    payload = payload || {};
    // prefer Schedule, then schedule, then payload itself
    const returnedSchedule = payload.schedule || payload;

    // prevent programmatic assignment from firing updateSchedule handlers
    this.suppressUpdate = true;

    // assign returned schedule (server authoritative)
    this.schedule = returnedSchedule;

    // reset errors
    this.errors = {};
    this.hasErrors = false;

    // handle ValidationResult if present
    const vr = payload.validationResult;
    if (vr) {
      const succeeded = vr.succeeded === true;
      if (!succeeded) this.hasErrors = true;

      // If identity-style errors array present, map them.
      // Clean any leading "key|" or "model.key|" prefix before storing the message.
      if (Array.isArray(vr.errors)) {
        console.log('[applySchedulePayload] Processing', vr.errors.length, 'validation errors');
        
        for (const rawErrText of vr.errors) {
          const errText = this.cleanErrorText(rawErrText);
          console.log('[applySchedulePayload] Raw error:', rawErrText);
          
          // Extract the key from "Key|Message" format
          const pipeIdx = rawErrText.indexOf('|');
          let errorKey = '';
          if (pipeIdx > 0) {
            errorKey = rawErrText.substring(0, pipeIdx).trim();
            // Remove "model." prefix if present
            errorKey = errorKey.replace(/^model\./i, '');
            console.log('[applySchedulePayload] Extracted key:', errorKey);
          }
          
          // Try to find a matching entry in errorMessageMap
          let matched = false;
          if (errorKey && this.errorMessageMap[errorKey]) {
            const group = this.errorMessageMap[errorKey].group;
            console.log('[applySchedulePayload] Matched to group:', group);
            this.errors[group] = this.errors[group] ? this.errors[group] + '\n' + errText : errText;
            matched = true;
          }
          
          if (!matched) {
            console.log('[applySchedulePayload] No match found for key:', errorKey, '- adding to General');
            // fallback to general
            this.errors['General'] = this.errors['General'] ? this.errors['General'] + '\n' + errText : errText;
          }
          
          // Also apply companion group highlighting for overlap errors
          this.applyOverlapCompanionGroups(errText);
        }
        
        console.log('[applySchedulePayload] Final errors object:', this.errors);
      }
    }

    // if server returned a modelState-style object, reuse existing parser
    if (payload.modelState || payload.ModelState) {
      const ms = payload.modelState || payload.ModelState;
      this.handleTempErrors(ms);
      this.hasErrors = true;
    }

    // format views and recompute dirty flag relative to originalValues
    this.formatSchedule();
    this.hasChanged = this.changedKeys.size > 0;

    // re-enable updates after programmatic assignment complete
    this.suppressUpdate = false;

    // Auto-scroll to first error card if there are errors
    if (this.hasErrors) {
      this.scrollToFirstErrorCard();
    }
  }

  // --- New helpers for highlighting used by template ---
  // returns 'edited-highlight' | 'error-highlight' | '' based on comparison and errors map
  getHighlightClassForField(fieldKey: string): string {
    if (!fieldKey) return '';
    // Error highlight first
    const mapKey = this.findErrorKeyForField(fieldKey);
    if (mapKey) {
      const group = this.errorMessageMap[mapKey].group;
      if (this.errors && this.errors[group]) return 'error-highlight';
    }
    // Edited highlight if explicitly marked dirty
    if (this.changedKeys.has(fieldKey)) return 'edited-highlight';
    return '';
  }

  // find an errorMessageMap key matching the provided field name (case-insensitive),
  // tries direct match, then PascalCase (capitalize first char).
  private findErrorKeyForField(fieldName: string): string | undefined {
    const keys = Object.keys(this.errorMessageMap);
    const lower = fieldName.toLowerCase();
    // Direct case-insensitive match
    for (const k of keys) {
      if (k.toLowerCase() === lower) return k;
    }
    // Try PascalCase (capitalize first char)
    const pascal = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
    for (const k of keys) {
      if (k === pascal) return k;
    }
    return undefined;
  }

  // compare schedule vs originalValues for a given base field name (accepts either camelCase or PascalCase)
  private isFieldChanged(fieldName: string): boolean {
    if (!this.originalValues) return false;
    // try camelCase field directly
    const a = (this.originalValues as any)[fieldName];
    const b = (this.schedule as any)[fieldName];
    if (typeof a !== 'undefined' || typeof b !== 'undefined') {
      return !AdminScheduleComponent.deepEqual(a, b);
    }
    // try PascalCase key from error map
    const mapKey = this.findErrorKeyForField(fieldName);
    if (mapKey) {
      const lowerKey = mapKey.charAt(0).toLowerCase() + mapKey.slice(1);
      const a2 = (this.originalValues as any)[lowerKey];
      const b2 = (this.schedule as any)[lowerKey];
      return !AdminScheduleComponent.deepEqual(a2, b2);
    }
    return false;
  }

  // Template-friendly: highlight a rendered date/view field (e.g. 'meatStoreItemPriceChangesWindowOpensNext')
  getHighlightClassForRenderedDate(viewFieldName: string): string {
    if (!viewFieldName) return '';
    const base = this.renderedDateToBase[viewFieldName];

    // Error highlight first (by base group)
    if (base) {
      const mapKey = this.findErrorKeyForField(base);
      if (mapKey) {
        const group = this.errorMessageMap[mapKey].group;
        if (this.errors && this.errors[group]) return 'error-highlight';
      }
    }

    // Edited highlight if the rendered date value itself changed vs originalValues
    const curr = (this.schedule as any)[viewFieldName];
    const orig = (this.originalValues as any)[viewFieldName];
    if (!AdminScheduleComponent.deepEqual(curr, orig)) return 'edited-highlight';

    // Also consider when we explicitly marked the base field as dirty (e.g. via forward/back buttons)
    if (base && this.changedKeys.has(base)) return 'edited-highlight';

    return '';
  }
  // --- end highlight helpers ---

  onTabClick(link) {
    this.activeLink = link;
    //if all are closed when switching tabs, open all so the tab change shows change
    if (!this.isWindowPanelOpen && !this.isTaskPanelOpen && !this.isOnDemandPanelOpen) {
      this.isWindowPanelOpen = true;
      this.isTaskPanelOpen = true;
      this.isOnDemandPanelOpen = true;
    }
  }

  isScheduleWindowOpen(scheduleWindowAsString) {
    // Use temporary schedule to evaluate window state so it reflects uncommitted changes
    this._scheduleService.isTemporaryScheduleWindowOpen(scheduleWindowAsString)
      .subscribe(
        (data: any) => {
          //console.log(scheduleWindowAsString + ':' + data.value);
          var name = 'is' + scheduleWindowAsString + 'Open';
          this[name] = data.value;
        },
        (response) => {
          this._messageService.onFailure('Failed to determine if "' + scheduleWindowAsString + '" is open.', response);
        }
      )
  }

  //currently not used.  commented out in view

  // invokePricingRulesProcess = function () {
  //   //Users with hasReadOnlyRestriction set to true should not even be allowed to invoke this action because it is hidden or disabled in the UI.
  //   //This is for extra protection. The back end will also be protected.
  //   if (this.myUser.hasReadOnlyRestriction){
  //     this._messageService.alertReadOnlyRestriction();
  //     return;
  //   }
  //

  //   this.confirmationDialogRef = this._dialog.open(ConfirmationDialogComponent, {
  //     disableClose: false,
  //     //width: '600px',
  //     data: {
  //       title: 'Important!',
  //       message: 'Are you sure you want to proceed with invoking the Pricing Rules process?',
  //       confirmText: 'Yes, proceed',
  //       cancelText: 'No, cancel this action!'
  //     }
  //   });
  //   this.confirmationDialogRef.afterClosed().subscribe(result => {
  //     if(result) {
  //

  //       this.pricingRulesMessage = 'Request Sent ' + moment().format('M/D/YYYY, h:mm:ss a');
  //       this._scheduleService.setProcessPricingRulesToNow()
  //         .subscribe(
  //           (data: any) => {
  //             this._messageService.onSuccess("'Process Pricing Rules' has been invoked.");
  //           },
  //           (response) => {
  //             this.pricingRulesMessage = 'Request Failed.';
  //             this._messageService.onFailure("Failed to invoke 'Process Pricing Rules.'", response);
  //           }
  //         )
  //

  //     }
  //     this.confirmationDialogRef = null;
  //   });
  //

  // };

  // Formats derived fields for display (TimeOfDayView + "Next" occurrence date strings + history)
  formatSchedule() {
    this.schedule.serverMaintenanceWindowOpensTimeOfDayView = this.getTimeString(this.schedule.serverMaintenanceWindowOpens);
    this.schedule.serverMaintenanceWindowClosesTimeOfDayView = this.getTimeString(this.schedule.serverMaintenanceWindowCloses);

    this.schedule.previewPricingWindowOpensTimeOfDayView = this.getTimeString(this.schedule.previewPricingWindowOpens);
    this.schedule.previewPricingWindowClosesTimeOfDayView = this.getTimeString(this.schedule.previewPricingWindowCloses);

    // this.schedule.meatAndProducePreviewPricingWindowOpensTimeOfDayView = this.getTimeString(this.schedule.meatAndProducePreviewPricingWindowOpens);
    // this.schedule.meatAndProducePreviewPricingWindowClosesTimeOfDayView = this.getTimeString(this.schedule.meatAndProducePreviewPricingWindowCloses);

    this.schedule.meatPreviewPricingWindowOpensTimeOfDayView = this.getTimeString(this.schedule.meatPreviewPricingWindowOpens);
    this.schedule.meatPreviewPricingWindowClosesTimeOfDayView = this.getTimeString(this.schedule.meatPreviewPricingWindowCloses);

    this.schedule.producePreviewPricingWindowOpensTimeOfDayView = this.getTimeString(this.schedule.producePreviewPricingWindowOpens);
    this.schedule.producePreviewPricingWindowClosesTimeOfDayView = this.getTimeString(this.schedule.producePreviewPricingWindowCloses);

    this.schedule.storeItemPriceChangesWindowOpensTimeOfDayView = this.getTimeString(this.schedule.storeItemPriceChangesWindowOpens);
    this.schedule.storeItemPriceChangesWindowClosesTimeOfDayView = this.getTimeString(this.schedule.storeItemPriceChangesWindowCloses);

    // this.schedule.meatAndProduceStoreItemPriceChangesWindowOpensTimeOfDayView = this.getTimeString(this.schedule.meatAndProduceStoreItemPriceChangesWindowOpens);
    // this.schedule.meatAndProduceStoreItemPriceChangesWindowClosesTimeOfDayView = this.getTimeString(this.schedule.meatAndProduceStoreItemPriceChangesWindowCloses);

    this.schedule.meatStoreItemPriceChangesWindowOpensTimeOfDayView = this.getTimeString(this.schedule.meatStoreItemPriceChangesWindowOpens);
    this.schedule.meatStoreItemPriceChangesWindowClosesTimeOfDayView = this.getTimeString(this.schedule.meatStoreItemPriceChangesWindowCloses);

    this.schedule.produceStoreItemPriceChangesWindowOpensTimeOfDayView = this.getTimeString(this.schedule.produceStoreItemPriceChangesWindowOpens);
    this.schedule.produceStoreItemPriceChangesWindowClosesTimeOfDayView = this.getTimeString(this.schedule.produceStoreItemPriceChangesWindowCloses);

    this.schedule.nowChangesLockoutWindowOpensTimeOfDayView = this.getTimeString(this.schedule.nowChangesLockoutWindowOpens);
    this.schedule.nowChangesLockoutWindowClosesTimeOfDayView = this.getTimeString(this.schedule.nowChangesLockoutWindowCloses);


    this.schedule.purgeOldLogActionsTimeOfDayView = this.getTimeString(this.schedule.purgeOldLogActions);
    this.schedule.purgeOldMessagesTimeOfDayView = this.getTimeString(this.schedule.purgeOldMessages);
    this.schedule.purgeOldAdItemsTimeOfDayView = this.getTimeString(this.schedule.purgeOldAdItems);
    this.schedule.purgeOldStoreItemPriceChangesTimeOfDayView = this.getTimeString(this.schedule.purgeOldStoreItemPriceChanges);
    this.schedule.purgeOldStoreItemCostChangesTimeOfDayView = this.getTimeString(this.schedule.purgeOldStoreItemCostChanges);
    this.schedule.purgeOldDenormalizedPreviewStoreItemPriceChangesTimeOfDayView = this.getTimeString(this.schedule.purgeOldDenormalizedPreviewStoreItemPriceChanges);
    this.schedule.updateStoreItemsFromPreviewStoreItemsTimeOfDayView = this.getTimeString(this.schedule.updateStoreItemsFromPreviewStoreItems);
    this.schedule.updateStoreItemsFromMeatAndProducePreviewStoreItemsTimeOfDayView = this.getTimeString(this.schedule.updateStoreItemsFromMeatAndProducePreviewStoreItems);
    this.schedule.deleteMeatAndProducePreviewStoreItemsTimeOfDayView = this.getTimeString(this.schedule.deleteMeatAndProducePreviewStoreItems);
    this.schedule.incrementStoreItemsWeeksInStoreTimeOfDayView = this.getTimeString(this.schedule.incrementStoreItemsWeeksInStore);
    this.schedule.resetStoreItemsMovementAndWeeksInStoreTimeOfDayView = this.getTimeString(this.schedule.resetStoreItemsMovementAndWeeksInStore);

    this.schedule.serverMaintenanceWindowOpensNext = moment(this.schedule.serverMaintenanceWindowOpens).format('M/D/YYYY');
    this.schedule.serverMaintenanceWindowClosesNext = moment(this.schedule.serverMaintenanceWindowCloses).format('M/D/YYYY');

    this.schedule.previewPricingWindowOpensNext = moment(this.schedule.previewPricingWindowOpens).format('M/D/YYYY');
    this.schedule.previewPricingWindowClosesNext = moment(this.schedule.previewPricingWindowCloses).format('M/D/YYYY');

    // this.schedule.meatAndProducePreviewPricingWindowOpensNext = moment(this.schedule.meatAndProducePreviewPricingWindowOpens).format('M/D/YYYY');
    // this.schedule.meatAndProducePreviewPricingWindowClosesNext = moment(this.schedule.meatAndProducePreviewPricingWindowCloses).format('M/D/YYYY');

    this.schedule.meatPreviewPricingWindowOpensNext = moment(this.schedule.meatPreviewPricingWindowOpens).format('M/D/YYYY');
    this.schedule.meatPreviewPricingWindowClosesNext = moment(this.schedule.meatPreviewPricingWindowCloses).format('M/D/YYYY');

    this.schedule.producePreviewPricingWindowOpensNext = moment(this.schedule.producePreviewPricingWindowOpens).format('M/D/YYYY');
    this.schedule.producePreviewPricingWindowClosesNext = moment(this.schedule.producePreviewPricingWindowCloses).format('M/D/YYYY');

    this.schedule.storeItemPriceChangesWindowOpensNext = moment(this.schedule.storeItemPriceChangesWindowOpens).format('M/D/YYYY');
    this.schedule.storeItemPriceChangesWindowClosesNext = moment(this.schedule.storeItemPriceChangesWindowCloses).format('M/D/YYYY');

    // this.schedule.meatAndProduceStoreItemPriceChangesWindowOpensNext = moment(this.schedule.meatAndProduceStoreItemPriceChangesWindowOpens).format('M/D/YYYY');
    // this.schedule.meatAndProduceStoreItemPriceChangesWindowClosesNext = moment(this.schedule.meatAndProduceStoreItemPriceChangesWindowCloses).format('M/D/YYYY');

    this.schedule.meatStoreItemPriceChangesWindowOpensNext = moment(this.schedule.meatStoreItemPriceChangesWindowOpens).format('M/D/YYYY');
    this.schedule.meatStoreItemPriceChangesWindowClosesNext = moment(this.schedule.meatStoreItemPriceChangesWindowCloses).format('M/D/YYYY');

    this.schedule.produceStoreItemPriceChangesWindowOpensNext = moment(this.schedule.produceStoreItemPriceChangesWindowOpens).format('M/D/YYYY');
    this.schedule.produceStoreItemPriceChangesWindowClosesNext = moment(this.schedule.produceStoreItemPriceChangesWindowCloses).format('M/D/YYYY');

    this.schedule.nowChangesLockoutWindowOpensNext = moment(this.schedule.nowChangesLockoutWindowOpens).format('M/D/YYYY');
    this.schedule.nowChangesLockoutWindowClosesNext = moment(this.schedule.nowChangesLockoutWindowCloses).format('M/D/YYYY');

    this.schedule.purgeOldLogActionsNext = moment(this.schedule.purgeOldLogActions).format('M/D/YYYY');
    this.schedule.purgeOldMessagesNext = moment(this.schedule.purgeOldMessages).format('M/D/YYYY');
    this.schedule.purgeOldAdItemsNext = moment(this.schedule.purgeOldAdItems).format('M/D/YYYY');
    this.schedule.purgeOldStoreItemPriceChangesNext = moment(this.schedule.purgeOldStoreItemPriceChanges).format('M/D/YYYY');
    this.schedule.purgeOldStoreItemCostChangesNext = moment(this.schedule.purgeOldStoreItemCostChanges).format('M/D/YYYY');
    this.schedule.purgeOldDenormalizedPreviewStoreItemPriceChangesNext = moment(this.schedule.purgeOldDenormalizedPreviewStoreItemPriceChanges).format('M/D/YYYY');
    this.schedule.updateStoreItemsFromPreviewStoreItemsNext = moment(this.schedule.updateStoreItemsFromPreviewStoreItems).format('M/D/YYYY');
    this.schedule.updateStoreItemsFromMeatAndProducePreviewStoreItemsNext = moment(this.schedule.updateStoreItemsFromMeatAndProducePreviewStoreItems).format('M/D/YYYY');
    this.schedule.deleteMeatAndProducePreviewStoreItemsNext = moment(this.schedule.deleteMeatAndProducePreviewStoreItems).format('M/D/YYYY');
    this.schedule.incrementStoreItemsWeeksInStoreNext = moment(this.schedule.incrementStoreItemsWeeksInStore).format('M/D/YYYY');
    this.schedule.resetStoreItemsMovementAndWeeksInStoreNext = moment(this.schedule.resetStoreItemsMovementAndWeeksInStore).format('M/D/YYYY');


    this.schedule.deleteMeatAndProducePreviewStoreItemsLastStarted = this.schedule.deleteMeatAndProducePreviewStoreItemsLastStarted != null ? moment(this.schedule.deleteMeatAndProducePreviewStoreItemsLastStarted).format('M/D/YYYY, h:mm:ss a') : 'N/A';
    this.schedule.incrementStoreItemsWeeksInStoreLastStarted = this.schedule.incrementStoreItemsWeeksInStoreLastStarted != null ? moment(this.schedule.incrementStoreItemsWeeksInStoreLastStarted).format('M/D/YYYY, h:mm:ss a') : 'N/A';
    this.schedule.purgeOldLogActionsLastStarted = this.schedule.purgeOldLogActionsLastStarted != null ? moment(this.schedule.purgeOldLogActionsLastStarted).format('M/D/YYYY, h:mm:ss a') : 'N/A';
    this.schedule.purgeOldMessagesLastStarted = this.schedule.purgeOldMessagesLastStarted != null ? moment(this.schedule.purgeOldMessagesLastStarted).format('M/D/YYYY, h:mm:ss a') : 'N/A';
    this.schedule.purgeOldAdItemsLastStarted = this.schedule.purgeOldAdItemsLastStarted != null ? moment(this.schedule.purgeOldAdItemsLastStarted).format('M/D/YYYY, h:mm:ss a') : 'N/A';
    this.schedule.purgeOldStoreItemPriceChangesLastStarted = this.schedule.purgeOldStoreItemPriceChangesLastStarted != null ? moment(this.schedule.purgeOldStoreItemPriceChangesLastStarted).format('M/D/YYYY, h:mm:ss a') : 'N/A';
    this.schedule.purgeOldStoreItemCostChangesLastStarted = this.schedule.purgeOldStoreItemCostChangesLastStarted != null ? moment(this.schedule.purgeOldStoreItemCostChangesLastStarted).format('M/D/YYYY, h:mm:ss a') : 'N/A';
    this.schedule.purgeOldDenormalizedPreviewStoreItemPriceChangesLastStarted = this.schedule.purgeOldDenormalizedPreviewStoreItemPriceChangesLastStarted != null ? moment(this.schedule.purgeOldDenormalizedPreviewStoreItemPriceChangesLastStarted).format('M/D/YYYY, h:mm:ss a') : 'N/A';
    this.schedule.resetStoreItemsMovementAndWeeksInStoreLastStarted = this.schedule.resetStoreItemsMovementAndWeeksInStoreLastStarted != null ? moment(this.schedule.resetStoreItemsMovementAndWeeksInStoreLastStarted).format('M/D/YYYY, h:mm:ss a') : 'N/A';
    this.schedule.updateStoreItemsFromMeatAndProducePreviewStoreItemsLastStarted = this.schedule.updateStoreItemsFromMeatAndProducePreviewStoreItemsLastStarted != null ? moment(this.schedule.updateStoreItemsFromMeatAndProducePreviewStoreItemsLastStarted).format('M/D/YYYY, h:mm:ss a') : 'N/A';
    this.schedule.updateStoreItemsFromPreviewStoreItemsLastStarted = this.schedule.updateStoreItemsFromPreviewStoreItemsLastStarted != null ? moment(this.schedule.updateStoreItemsFromPreviewStoreItemsLastStarted).format('M/D/YYYY, h:mm:ss a') : 'N/A';
    this.schedule.processPricingRulesLastStarted = this.schedule.processPricingRulesLastStarted != null ? moment(this.schedule.processPricingRulesLastStarted).format('M/D/YYYY, h:mm:ss a') : 'N/A';

    this.schedule.deleteMeatAndProducePreviewStoreItemsLastCompleted = this.schedule.deleteMeatAndProducePreviewStoreItemsLastCompleted != null ? moment(this.schedule.deleteMeatAndProducePreviewStoreItemsLastCompleted).format('M/D/YYYY, h:mm:ss a') : 'N/A';
    this.schedule.incrementStoreItemsWeeksInStoreLastCompleted = this.schedule.incrementStoreItemsWeeksInStoreLastCompleted != null ? moment(this.schedule.incrementStoreItemsWeeksInStoreLastCompleted).format('M/D/YYYY, h:mm:ss a') : 'N/A';
    this.schedule.purgeOldLogActionsLastCompleted = this.schedule.purgeOldLogActionsLastCompleted != null ? moment(this.schedule.purgeOldLogActionsLastCompleted).format('M/D/YYYY, h:mm:ss a') : 'N/A';
    this.schedule.purgeOldMessagesLastCompleted = this.schedule.purgeOldMessagesLastCompleted != null ? moment(this.schedule.purgeOldMessagesLastCompleted).format('M/D/YYYY, h:mm:ss a') : 'N/A';
    this.schedule.purgeOldAdItemsLastCompleted = this.schedule.purgeOldAdItemsLastCompleted != null ? moment(this.schedule.purgeOldAdItemsLastCompleted).format('M/D/YYYY, h:mm:ss a') : 'N/A';
    this.schedule.purgeOldStoreItemPriceChangesLastCompleted = this.schedule.purgeOldStoreItemPriceChangesLastCompleted != null ? moment(this.schedule.purgeOldStoreItemPriceChangesLastCompleted).format('M/D/YYYY, h:mm:ss a') : 'N/A';
    this.schedule.purgeOldStoreItemCostChangesLastCompleted = this.schedule.purgeOldStoreItemCostChangesLastCompleted != null ? moment(this.schedule.purgeOldStoreItemCostChangesLastCompleted).format('M/D/YYYY, h:mm:ss a') : 'N/A';
    this.schedule.purgeOldDenormalizedPreviewStoreItemPriceChangesLastCompleted = this.schedule.purgeOldDenormalizedPreviewStoreItemPriceChangesLastCompleted != null ? moment(this.schedule.purgeOldDenormalizedPreviewStoreItemPriceChangesLastCompleted).format('M/D/YYYY, h:mm:ss a') : 'N/A';
    this.schedule.resetStoreItemsMovementAndWeeksInStoreLastCompleted = this.schedule.resetStoreItemsMovementAndWeeksInStoreLastCompleted != null ? moment(this.schedule.resetStoreItemsMovementAndWeeksInStoreLastCompleted).format('M/D/YYYY, h:mm:ss a') : 'N/A';
    this.schedule.updateStoreItemsFromMeatAndProducePreviewStoreItemsLastCompleted = this.schedule.updateStoreItemsFromMeatAndProducePreviewStoreItemsLastCompleted != null ? moment(this.schedule.updateStoreItemsFromMeatAndProducePreviewStoreItemsLastCompleted).format('M/D/YYYY, h:mm:ss a') : 'N/A';
    this.schedule.updateStoreItemsFromPreviewStoreItemsLastCompleted = this.schedule.updateStoreItemsFromPreviewStoreItemsLastCompleted != null ? moment(this.schedule.updateStoreItemsFromPreviewStoreItemsLastCompleted).format('M/D/YYYY, h:mm:ss a') : 'N/A';
    this.schedule.processPricingRulesLastCompleted = this.schedule.processPricingRulesLastCompleted != null ? moment(this.schedule.processPricingRulesLastCompleted).format('M/D/YYYY, h:mm:ss a') : 'N/A';

    // Uncomment the following to test these
    // this.schedule.deleteMeatAndProducePreviewStoreItemsLastExecutionSuccessful = true;
    // this.schedule.incrementStoreItemsWeeksInStoreLastExecutionSuccessful = true;
    // this.schedule.processPricingRulesLastExecutionSuccessful = true;
    // this.schedule.purgeOldLogActionsLastExecutionSuccessful = true;
    // this.schedule.purgeOldMessagesLastExecutionSuccessful = null;
    // this.schedule.purgeOldAdItemsLastExecutionSuccessful = null;
    // this.schedule.purgeOldStoreItemPriceChangesLastExecutionSuccessful = true;
    // this.schedule.purgeOldStoreItemCostChangesLastExecutionSuccessful = false;
    // this.schedule.resetStoreItemsMovementAndWeeksInStoreLastExecutionSuccessful = false;
    // this.schedule.updateStoreItemsFromMeatAndProducePreviewStoreItemsLastExecutionSuccessful = false;
    // this.schedule.updateStoreItemsFromPreviewStoreItemsLastExecutionSuccessful = false;
    // this.schedule.processPricingRulesLastExecutionSuccessful = false;

    // Uncomment to test in process overlay
    // this.schedule.processPricingRulesExecutionRequested = '2019-03-01T13:03:05.17';
    // this.schedule.resetStoreItemsMovementAndWeeksInStoreExecutionRequested = '2019-03-01T13:03:05.17';
    // this.schedule.incrementStoreItemsWeeksInStoreExecutionRequested = '2019-03-01T13:03:05.17';
    // this.schedule.deleteMeatAndProducePreviewStoreItemsExecutionRequested = '2019-03-01T13:03:05.17';
    // this.schedule.updateStoreItemsFromMeatAndProducePreviewStoreItemsExecutionRequested = '2019-03-01T13:03:05.17';
    // this.schedule.updateStoreItemsFromPreviewStoreItemsExecutionRequested = '2019-03-01T13:03:05.17';
    // this.schedule.purgeOldStoreItemCostChangesExecutionRequested = '2019-03-01T13:03:05.17';
    // this.schedule.purgeOldStoreItemPriceChangesExecutionRequested = '2019-03-01T13:03:05.17';
    // this.schedule.purgeOldAdItemsExecutionRequested = '2019-03-01T13:03:05.17';
    // this.schedule.purgeOldMessagesExecutionRequested = '2019-03-01T13:03:05.17';
    // this.schedule.purgeOldLogActionsExecutionRequested = '2019-03-01T13:03:05.17';

  }

  formatDate(d) {
    return d ? moment(d).format('M/D/YYYY, h:mm:ss a') : null;
  };

  getTimeString(date) {
    date = new Date(date);
    var hours = date.getHours();
    if (hours < 10) hours = '0' + hours;
    var minutes = date.getMinutes();
    if (minutes < 10) minutes = '0' + minutes;
    return hours + ':' + minutes;
  }

  // Enqueue a debounced update (called from template). Returns immediately if programmatic assignment in progress.
  // This posts changes to the TEMPORARY schedule only.
  updateSchedule() {
    const seq = ++this.reqSeq;
    console.log(`[AdminSchedule] updateSchedule() seq=${seq} start`, {
      suppressUpdate: this.suppressUpdate,
      autosavePaused: this.autosavePaused,
      validationBlocked: this.validationBlocked,
      inFlightUpdate: this.inFlightUpdate
    });

    // Guard skip if programmatic assignment in progress
    if (this.suppressUpdate) {
      console.log(`[AdminSchedule] updateSchedule() seq=${seq} early return (suppressUpdate)`);
      return;
    }

    // While Save is in progress (or awaiting authoritative reload), don't autosave
    if (this.autosavePaused) {
      console.log(`[AdminSchedule] updateSchedule() seq=${seq} early return (autosavePaused)`);
      return;
    }

    // If an error is blocking, only allow retry once implicated card fields have changed since the error.
    if (this.validationBlocked) {
      const hasChanges = this.hasChangesInBlockedGroups();
      console.log(`[AdminSchedule] updateSchedule() seq=${seq} validationBlocked=${this.validationBlocked} hasChangesInBlockedGroups=${hasChanges}`);
      if (!hasChanges) {
        return; // don't call server yet
      }
      // user has edited something inside the blocked groups; clear the block and proceed
      this.validationBlocked = false;
      this.blockedGroups.clear();
      this.lastErrorScheduleSnapshot = null;
      console.log(`[AdminSchedule] updateSchedule() seq=${seq} cleared validation block after user edit`);
    }

    if (this.inFlightUpdate) {
      this.pendingChange = true;
      console.log(`[AdminSchedule] updateSchedule() seq=${seq} inFlightUpdate=true -> pendingChange=true`);
    }

    // push a signal into debounced subject
    console.log(`[AdminSchedule] updateSchedule() seq=${seq} enqueue debounced commit`);
    this.updateSchedule$.next();
  }

  // --- commitUpdateSchedule(): ignore autosave while paused and drop any in-flight responses during Save ---
  private commitUpdateSchedule() {
    const seq = ++this.reqSeq;
    console.log(`[AdminSchedule] commitUpdateSchedule() seq=${seq} start`, {
      suppressUpdate: this.suppressUpdate,
      autosavePaused: this.autosavePaused,
      inFlightUpdate: this.inFlightUpdate
    });

    if (this.suppressUpdate || this.autosavePaused) {
      console.log(`[AdminSchedule] commitUpdateSchedule() seq=${seq} early return (suppressUpdate || autosavePaused)`);
      return;
    }

    if (this.inFlightUpdate) {
      this.pendingChange = true;
      console.log(`[AdminSchedule] commitUpdateSchedule() seq=${seq} inFlightUpdate=true -> pendingChange=true`);
      return;
    }

    if (this.myUser.hasReadOnlyRestriction) {
      console.log(`[AdminSchedule] commitUpdateSchedule() seq=${seq} read-only guard hit`);
      this._messageService.alertReadOnlyRestriction();
      return;
    }

    const outgoing = this.buildOutgoingScheduleSnapshot();
    const payloadStr = JSON.stringify(outgoing);
    if (payloadStr === this.lastSentPayload) {
      console.log(`[AdminSchedule] commitUpdateSchedule() seq=${seq} deduped same payload`);
      return;
    }

    this.inFlightUpdate = true;
    this.lastSentPayload = payloadStr;
    console.log(`[AdminSchedule] commitUpdateSchedule() seq=${seq} sending PUT /UpdateSchedule`);

    this._scheduleService.updateSchedule(outgoing)
      .subscribe(
        (data) => {
          // If autosave is paused (e.g., Save button flow), ignore this response
          if (this.autosavePaused) {
            console.log(`[AdminSchedule] commitUpdateSchedule() seq=${seq} response ignored (autosavePaused)`);
            this.inFlightUpdate = false;
            this.pendingChange = false;
            return;
          }

          console.log(`[AdminSchedule] commitUpdateSchedule() seq=${seq} success`);
          this.validationBlocked = false;
          this.blockedGroups.clear();
          this.lastErrorScheduleSnapshot = null;
          this.hasErrors = false; // Clear error flag on successful autosave
          this.errors = {}; // Clear all error messages

          // Refresh temporary snapshot from server to reflect any server-side normalization.
          // This does NOT reset dirty tracking (we keep showing the user's changed state).
          this.getTemporarySchedule();

          this.inFlightUpdate = false;

          if (this.pendingChange) {
            console.log(`[AdminSchedule] commitUpdateSchedule() seq=${seq} flushing pendingChange`);
            this.pendingChange = false;
            this.updateSchedule$.next();
          }
        },
        (response) => {
          // If autosave is paused (e.g., Save button flow), ignore this response too
          if (this.autosavePaused) {
            console.log(`[AdminSchedule] commitUpdateSchedule() seq=${seq} error ignored (autosavePaused)`);
            this.inFlightUpdate = false;
            this.pendingChange = false;
            return;
          }

          this.hasErrors = true;
          console.log(`[AdminSchedule] commitUpdateSchedule() seq=${seq} error`, {
            status: response?.status,
            modelState: !!(response && response.error && response.error.modelState)
          });
          this._messageService.showErrorToastTopRight('Check page for conflicts.');

          if (response && response.error && response.error.modelState) {
            this.handleTempErrors(response.error.modelState);
          }

          this.validationBlocked = true;
          this.blockedGroups = new Set(Object.keys(this.errors).filter(g => !!g && g !== 'General'));
          this.lastErrorScheduleSnapshot = JSON.parse(JSON.stringify(this.schedule));
          console.log(`[AdminSchedule] commitUpdateSchedule() seq=${seq} set validationBlocked, blockedGroups=`, Array.from(this.blockedGroups));

          // Do NOT refresh schedule from server on error; keep user edits visible
          this.inFlightUpdate = false;

          // Do not auto-resend; wait for user edits in implicated cards
          this.pendingChange = false;
        }
      )
  };

  handleTempErrors(errs) {
    this.errors = {}; // reset errors object

    for (const [key, value] of Object.entries(errs)) {
      const cleanKey = key.replace(/^model\./i, ""); // remove "model." prefix (case-insensitive)
      const mapEntry = this.errorMessageMap[cleanKey]; // lookup from error map

      if (mapEntry) {
        const group = mapEntry.group;
        // Ensure value is an array before joining
        const msg = Array.isArray(value) ? value.join("\n") : String(value);

        // Strip any leading 'key|' segments from each line if present.
        const cleaned = msg.split('\n').map(line => {
          const pipeIdx = line.indexOf('|');
          if (pipeIdx > 0) {
            const prefix = line.substring(0, pipeIdx).trim();
            if (prefix.toLowerCase() === cleanKey.toLowerCase() || prefix.toLowerCase() === ('model.' + cleanKey).toLowerCase()) {
              return line.substring(pipeIdx + 1).trim();
            }
          }
          return line;
        }).join('\n');

        // Assign to the primary group
        this.errors[group] = this.errors[group]
          ? this.errors[group] + "\n" + cleaned
          : cleaned;

        // Also mirror overlap errors into referenced companion groups so both cards turn red
        this.applyOverlapCompanionGroups(cleaned);
      } else {
        // If no map entry found, still try to clean and place into General
        const msg = Array.isArray(value) ? value.join("\n") : String(value);
        const cleaned = msg.split('\n').map(line => {
          const pipeIdx = line.indexOf('|');
          if (pipeIdx > 0) {
            // strip common "model.X|" or "X|" prefixes regardless of map presence
            return line.substring(pipeIdx + 1).trim();
          }
          return line;
        }).join('\n');
        this.errors['General'] = this.errors['General']
          ? this.errors['General'] + '\n' + cleaned
          : cleaned;

        // Try companion mapping even for general text
        this.applyOverlapCompanionGroups(cleaned);
      }
    }

    console.log(this.errors);

    // Auto-scroll to first error card
    this.scrollToFirstErrorCard();
  }

  // Duplicate "must not overlap" errors into all referenced cards to ensure both highlight
  private applyOverlapCompanionGroups(message: string) {
    if (!message) return;

    // Only process overlap errors (window vs window), not task timing errors
    if (message.indexOf('must not overlap with') < 0) {
      return; // This is a task timing error, don't distribute to windows
    }

    // map substrings in error messages to our error groups
    // Order matters! Check more specific tokens first to avoid substring matches
    // Use the exact UI titles as shown on the schedule page
    const tokenToGroup: Record<string, string> = {
      'Weekly Branded Meat Preview Pricing Window': 'MeatPreviewPricingWindow',
      'Weekly Branded Meat Store Item Price Changes Window': 'MeatStoreItemPriceChangesWindow',
      'Weekly Produce Preview Pricing Window': 'ProducePreviewPricingWindow',
      'Weekly Produce Store Item Price Changes Window': 'ProduceStoreItemPriceChangesWindow',
      'Weekly Grocery Preview Pricing Window': 'PreviewPricingWindow',
      'Weekly Grocery Store Item Price Changes Window': 'StoreItemPriceChangesWindow',
    };

    const touchedGroups = new Set<string>();
    
    // Check tokens in order, more specific first
    for (const token of Object.keys(tokenToGroup)) {
      if (message.indexOf(token) >= 0) {
        touchedGroups.add(tokenToGroup[token]);
      }
    }

    // If at least one group token was found, put the same message on all involved groups
    // BUT avoid duplicating if the message is already present
    if (touchedGroups.size > 0) {
      for (const g of touchedGroups) {
        if (!g) continue;
        
        // Check if this exact message is already in the group's errors
        const existingError = this.errors[g] || '';
        if (!existingError.includes(message)) {
          this.errors[g] = existingError
            ? existingError + '\n' + message
            : message;
        }
      }
    }
  }

  // SAVE BUTTON: Commit the TEMPORARY schedule, then reload the authoritative schedule.
  // Rationale:
  //  - We pause autosave to avoid blur-driven autosave races while the user is clicking Save.
  //  - On success, we call getSchedule to populate fields & controls from the PERSISTENT schedule.
  //  - We do NOT reset dirty tracking here; that is done after getSchedule applies authoritative data.
  //  - This ensures the UI state exactly mirrors persistent state post-save.
  onSave() {
    // Prevent double-click spam while a save is in-flight
    if (this.isSaving) return;

    // Read-only guard (extra protection beyond UI disablement).
    if (this.myUser?.hasReadOnlyRestriction) {
      this._messageService.alertReadOnlyRestriction();
      return;
    }

    console.log(this.hasChanged);
    console.log(this.originalValues);
    console.log(this.schedule);

    // Set in-flight save guard
    this.isSaving = true;

    // Pause autosave to avoid blur/selectionChange and in-flight autosave races during Save
    this.autosavePaused = true;
    // Signal getSchedule() to un-pause after it finishes (success path)
    this.resumeAutosaveAfterLoad = true;

    this._scheduleService.commitTemporarySchedule()
      .pipe(
        // Surface hung requests (never reaching server or swallowed by an interceptor) as an error
        timeout(15000),
        // Always run to ensure we do not leave UI locked if neither next nor error runs
        finalize(() => {
          // Dismiss wait spinner
          this._spinnerService.handleProgressSpinnerVisibility('hide');
        })
      )
      .subscribe(
        // Backend now ALWAYS returns HTTP 200 with ScheduleWithValidationReturnModel
        (data: any) => {
          const vr = data?.validationResult || data?.ValidationResult;
          
          // Check if validation failed
          if (vr && vr.succeeded === false) {
            // Re-enable autosave so user edits can go back through temporary update flow
            this.autosavePaused = false;
            this.resumeAutosaveAfterLoad = false;

            // Apply payload (maps errors & formats schedule)
            this.applySchedulePayload(data);
            
            // Update originalValues to the current schedule state so future fixes are tracked as changes
            this.originalValues = JSON.parse(JSON.stringify(this.schedule || {}));
            this.changedKeys.clear();
            this.hasChanged = false;
            
            this.hasErrors = true;
            this.validationBlocked = true;
            this.blockedGroups = new Set(Object.keys(this.errors).filter(g => !!g && g !== 'General'));
            this.lastErrorScheduleSnapshot = JSON.parse(JSON.stringify(this.schedule));
            console.log('[AdminSchedule] onSave() validationBlocked set, blockedGroups=', Array.from(this.blockedGroups));
            this._messageService.showErrorToastTopRight('Please fix the highlighted errors.');
            this.isSaving = false;
            
            // Auto-scroll to first error card
            this.scrollToFirstErrorCard();
            return;
          }

          // Normal success path
          this._messageService.onSuccess('Successfully updated schedule');
          // Load authoritative schedule; getSchedule will clear isSaving and resume autosave
          this.getSchedule();
        },
        // ERROR handler now only for network/server errors (not validation errors)
        (response) => {
          console.log('[AdminSchedule] onSave() network/server error', { status: response?.status });

          // On error, re-enable autosave and clear flags
          this.autosavePaused = false;
          this.resumeAutosaveAfterLoad = false;
          this.isSaving = false;

          // Generic error message for infrastructure failures
          this._messageService.onFailure('Failed to save schedule. Please try again.', response);
        }
      );
  }

  onBackwardOneDay = function (eventAsString) {
    if (this.myUser.hasReadOnlyRestriction) {
      this._messageService.alertReadOnlyRestriction();
      return;
    }
    this._scheduleService.setDailyScheduledEventBackwardOneDay(eventAsString)
      .subscribe(
        (data: any) => {
          this.applySchedulePayload(data);
          this.markEventChanged(eventAsString);
        },
        (response) => {
          this._messageService.onFailure('Failed to set scheduled event backward one day.', response);
        }
      )
  };

  onForwardOneDay = function (eventAsString) {
    if (this.myUser.hasReadOnlyRestriction) {
      this._messageService.alertReadOnlyRestriction();
      return;
    }
    this._scheduleService.setDailyScheduledEventForwardOneDay(eventAsString)
      .subscribe(
        (data: any) => {
          this.applySchedulePayload(data);
          this.markEventChanged(eventAsString);
        },
        (response) => {
          this._messageService.onFailure('Failed to set scheduled event forward one day.', response);
        }
      )
  };

  onBackwardOneWeek = function (eventAsString) {
    if (this.myUser.hasReadOnlyRestriction) {
      this._messageService.alertReadOnlyRestriction();
      return;
    }
    this._scheduleService.setWeeklyScheduledEventBackwardOneWeek(eventAsString)
      .subscribe(
        (data: any) => {
          this.applySchedulePayload(data);
          this.markEventChanged(eventAsString);
        },
        (response) => {
          this._messageService.onFailure('Failed to set scheduled event backward one week.', response);
        }
      )
  };

  onForwardOneWeek = function (eventAsString) {
    if (this.myUser.hasReadOnlyRestriction) {
      this._messageService.alertReadOnlyRestriction();
      return;
    }
    this._scheduleService.setWeeklyScheduledEventForwardOneWeek(eventAsString)
      .subscribe(
        (data: any) => {
          this.applySchedulePayload(data);
          this.markEventChanged(eventAsString);
        },
        (response) => {
          this._messageService.onFailure('Failed to set scheduled event forward one week.', response);
        }
      )
  };

  onBackwardOneYear = function (eventAsString) {
    if (this.myUser.hasReadOnlyRestriction) {
      this._messageService.alertReadOnlyRestriction();
      return;
    }
    this._scheduleService.setYearlyScheduledEventBackwardOneYear(eventAsString)
      .subscribe(
        (data: any) => {
          this.applySchedulePayload(data);
          this.markEventChanged(eventAsString);
        },
        (response) => {
          this._messageService.onFailure('Failed to set scheduled event backward one year', response);
        }
      )
  };

  onForwardOneYear = function (eventAsString) {
    if (this.myUser.hasReadOnlyRestriction) {
      this._messageService.alertReadOnlyRestriction();
      return;
    }
    this._scheduleService.setYearlyScheduledEventForwardOneYear(eventAsString)
      .subscribe(
        (data: any) => {
          this.applySchedulePayload(data);
          this.markEventChanged(eventAsString);
        },
        (response) => {
          this._messageService.onFailure('Failed to set scheduled event forward one year', response);
        }
      )
  };

  // New helper added to class: cleanErrorText
  // Removes leading "Key|" or "model.Key|" prefixes from server error strings.
  private cleanErrorText(errText: any, keyGuess?: string): string {
    if (!errText) return errText;
    let text = String(errText);

    const pipeIdx = text.indexOf('|');
    if (pipeIdx > 0) {
      const prefix = text.substring(0, pipeIdx).trim();
      if (keyGuess) {
        if (prefix.toLowerCase() === keyGuess.toLowerCase() || prefix.toLowerCase() === ('model.' + keyGuess).toLowerCase()) {
          return text.substring(pipeIdx + 1).trim();
        }
      } else {
        // If no keyGuess passed, strip obvious model.<key>| prefixes or plain <key>| 
        if (/^model\.[A-Za-z0-9_]+$/i.test(prefix) || /^[A-Za-z0-9_]+$/i.test(prefix)) {
          return text.substring(pipeIdx + 1).trim();
        }
      }
    }
    return text;
  }

  // Resets dirty tracking after initial load or a successful save
  private resetDirtyTracking(): void {
    this.changedKeys.clear();
    this.hasChanged = false;
    this.originalValues = JSON.parse(JSON.stringify(this.schedule || {}));
  }

  // Call from template on control edits, e.g. (change) or (blur)
  public onModelChange(fieldKey: string): void {
    if (!fieldKey) return;
    const curr = (this.schedule as any)[fieldKey];
    const orig = (this.originalValues as any)[fieldKey];
    if (!AdminScheduleComponent.deepEqual(curr, orig)) {
      this.changedKeys.add(fieldKey);
      this.hasChanged = true;
    }
    this.updateSchedule();
  }

  // Map server event names -> base schedule field to mark as changed
  private eventToBaseField: Record<string, string> = {
    ServerMaintenanceWindowOpens: 'serverMaintenanceWindowOpens',
    ServerMaintenanceWindowCloses: 'serverMaintenanceWindowCloses',
    PreviewPricingWindowOpens: 'previewPricingWindowOpens',
    PreviewPricingWindowCloses: 'previewPricingWindowCloses',
    MeatPreviewPricingWindowOpens: 'meatPreviewPricingWindowOpens',
    MeatPreviewPricingWindowCloses: 'meatPreviewPricingWindowCloses',
    ProducePreviewPricingWindowOpens: 'producePreviewPricingWindowOpens',
    ProducePreviewPricingWindowCloses: 'producePreviewPricingWindowCloses',
    StoreItemPriceChangesWindowOpens: 'storeItemPriceChangesWindowOpens',
    StoreItemPriceChangesWindowCloses: 'storeItemPriceChangesWindowCloses',
    MeatStoreItemPriceChangesWindowOpens: 'meatStoreItemPriceChangesWindowOpens',
    MeatStoreItemPriceChangesWindowCloses: 'meatStoreItemPriceChangesWindowCloses',
    ProduceStoreItemPriceChangesWindowOpens: 'produceStoreItemPriceChangesWindowOpens',
    ProduceStoreItemPriceChangesWindowCloses: 'produceStoreItemPriceChangesWindowCloses',
    NowChangesLockoutWindowOpens: 'nowChangesLockoutWindowOpens',
    NowChangesLockoutWindowCloses: 'nowChangesLockoutWindowCloses',
    PurgeOldLogActions: 'purgeOldLogActions',
    PurgeOldMessages: 'purgeOldMessages',
    PurgeOldAdItems: 'purgeOldAdItems',
    PurgeOldStoreItemPriceChanges: 'purgeOldStoreItemPriceChanges',
    PurgeOldStoreItemCostChanges: 'purgeOldStoreItemCostChanges',
    PurgeOldDenormalizedPreviewStoreItemPriceChanges: 'purgeOldDenormalizedPreviewStoreItemPriceChanges',
    UpdateStoreItemsFromPreviewStoreItems: 'updateStoreItemsFromPreviewStoreItems',
    UpdateStoreItemsFromMeatAndProducePreviewStoreItems: 'updateStoreItemsFromMeatAndProducePreviewStoreItems',
    DeleteMeatAndProducePreviewStoreItems: 'deleteMeatAndProducePreviewStoreItems',
    IncrementStoreItemsWeeksInStore: 'incrementStoreItemsWeeksInStore',
    ResetStoreItemsMovementAndWeeksInStore: 'resetStoreItemsMovementAndWeeksInStore'
  };

  private markEventChanged(eventName: string): void {
    const base = this.eventToBaseField[eventName];
    if (base) {
      this.changedKeys.add(base);
      this.hasChanged = true;
    }
  }

  // Returns a lower-cased group name prefix used to match schedule fields for that card/group
  private groupPrefix(group: string): string {
    if (!group) return '';
    return group.charAt(0).toLowerCase() + group.slice(1);
  }

  // Check if any field under blocked groups has changed since the last error snapshot
  private hasChangesInBlockedGroups(): boolean {
    if (!this.lastErrorScheduleSnapshot || this.blockedGroups.size === 0) return true;
    const prev: any = this.lastErrorScheduleSnapshot;
    const curr: any = this.schedule;

    for (const group of this.blockedGroups) {
      if (!group || group === 'General') continue;
      const prefix = this.groupPrefix(group);
      if (!prefix) continue;
      const keys = Object.keys(curr).filter(k => k.startsWith(prefix));
      for (const k of keys) {
        if (!AdminScheduleComponent.deepEqual(curr[k], prev[k])) {
          return true; // something under this group changed
        }
      }
    }
    return false;
  }

  // Build a shallow outgoing snapshot for sending to server; avoids mutating this.schedule directly
  private buildOutgoingScheduleSnapshot(): any {
    const s = JSON.parse(JSON.stringify(this.schedule || {}));

    // map TimeOfDayView -> TimeOfDay for outgoing payload (same logic as before)
    s.previewPricingWindowOpensTimeOfDay = s.previewPricingWindowOpensTimeOfDayView != null ? s.previewPricingWindowOpensTimeOfDayView : '00:00';
    s.previewPricingWindowClosesTimeOfDay = s.previewPricingWindowClosesTimeOfDayView != null ? s.previewPricingWindowClosesTimeOfDayView : '00:00';

    // s.meatAndProducePreviewPricingWindowOpensTimeOfDay = s.meatAndProducePreviewPricingWindowOpensTimeOfDayView != null ? s.meatAndProducePreviewPricingWindowOpensTimeOfDayView : '00:00';
    // s.meatAndProducePreviewPricingWindowClosesTimeOfDay = s.meatAndProducePreviewPricingWindowClosesTimeOfDayView != null ? s.meatAndProducePreviewPricingWindowClosesTimeOfDayView : '00:00';

    s.meatPreviewPricingWindowOpensTimeOfDay = s.meatPreviewPricingWindowOpensTimeOfDayView != null ? s.meatPreviewPricingWindowOpensTimeOfDayView : '00:00';
    s.meatPreviewPricingWindowClosesTimeOfDay = s.meatPreviewPricingWindowClosesTimeOfDayView != null ? s.meatPreviewPricingWindowClosesTimeOfDayView : '00:00';

    s.producePreviewPricingWindowOpensTimeOfDay = s.producePreviewPricingWindowOpensTimeOfDayView != null ? s.producePreviewPricingWindowOpensTimeOfDayView : '00:00';
    s.producePreviewPricingWindowClosesTimeOfDay = s.producePreviewPricingWindowClosesTimeOfDayView != null ? s.producePreviewPricingWindowClosesTimeOfDayView : '00:00';

    s.storeItemPriceChangesWindowOpensTimeOfDay = s.storeItemPriceChangesWindowOpensTimeOfDayView != null ? s.storeItemPriceChangesWindowOpensTimeOfDayView : '00:00';
    s.storeItemPriceChangesWindowClosesTimeOfDay = s.storeItemPriceChangesWindowClosesTimeOfDayView != null ? s.storeItemPriceChangesWindowClosesTimeOfDayView : '00:00';

    // s.meatAndProduceStoreItemPriceChangesWindowOpensTimeOfDay = s.meatAndProduceStoreItemPriceChangesWindowOpensTimeOfDayView != null ? s.meatAndProduceStoreItemPriceChangesWindowOpensTimeOfDayView : '00:00';
    // s.meatAndProduceStoreItemPriceChangesWindowClosesTimeOfDay = s.meatAndProduceStoreItemPriceChangesWindowClosesTimeOfDayView != null ? s.meatAndProduceStoreItemPriceChangesWindowClosesTimeOfDayView : '00:00';

    s.meatStoreItemPriceChangesWindowOpensTimeOfDay = s.meatStoreItemPriceChangesWindowOpensTimeOfDayView != null ? s.meatStoreItemPriceChangesWindowOpensTimeOfDayView : '00:00';
    s.meatStoreItemPriceChangesWindowClosesTimeOfDay = s.meatStoreItemPriceChangesWindowClosesTimeOfDayView != null ? s.meatStoreItemPriceChangesWindowClosesTimeOfDayView : '00:00';

    s.produceStoreItemPriceChangesWindowOpensTimeOfDay = s.produceStoreItemPriceChangesWindowOpensTimeOfDayView != null ? s.produceStoreItemPriceChangesWindowOpensTimeOfDayView : '00:00';
    s.produceStoreItemPriceChangesWindowClosesTimeOfDay = s.produceStoreItemPriceChangesWindowClosesTimeOfDayView != null ? s.produceStoreItemPriceChangesWindowClosesTimeOfDayView : '00:00';

    s.nowChangesLockoutWindowOpensTimeOfDay = s.nowChangesLockoutWindowOpensTimeOfDayView != null ? s.nowChangesLockoutWindowOpensTimeOfDayView : '00:00';
    s.nowChangesLockoutWindowClosesTimeOfDay = s.nowChangesLockoutWindowClosesTimeOfDayView != null ? s.nowChangesLockoutWindowClosesTimeOfDayView : '00:00';

    s.serverMaintenanceWindowOpensTimeOfDay = s.serverMaintenanceWindowOpensTimeOfDayView != null ? s.serverMaintenanceWindowOpensTimeOfDayView : '00:00';
    s.serverMaintenanceWindowClosesTimeOfDay = s.serverMaintenanceWindowClosesTimeOfDayView != null ? s.serverMaintenanceWindowClosesTimeOfDayView : '00:00';

    s.updateStoreItemsFromPreviewStoreItemsTimeOfDay = s.updateStoreItemsFromPreviewStoreItemsTimeOfDayView != null ? s.updateStoreItemsFromPreviewStoreItemsTimeOfDayView : '00:00';
    s.updateStoreItemsFromMeatAndProducePreviewStoreItemsTimeOfDay = s.updateStoreItemsFromMeatAndProducePreviewStoreItemsTimeOfDayView != null ? s.updateStoreItemsFromMeatAndProducePreviewStoreItemsTimeOfDayView : '00:00';
    s.deleteMeatAndProducePreviewStoreItemsTimeOfDay = s.deleteMeatAndProducePreviewStoreItemsTimeOfDayView != null ? s.deleteMeatAndProducePreviewStoreItemsTimeOfDayView : '00:00';
    s.incrementStoreItemsWeeksInStoreTimeOfDay = s.incrementStoreItemsWeeksInStoreTimeOfDayView != null ? s.incrementStoreItemsWeeksInStoreTimeOfDayView : '00:00';
    s.resetStoreItemsMovementAndWeeksInStoreTimeOfDay = s.resetStoreItemsMovementAndWeeksInStoreTimeOfDayView != null ? s.resetStoreItemsMovementAndWeeksInStoreTimeOfDayView : '00:00';
    s.purgeOldLogActionsTimeOfDay = s.purgeOldLogActionsTimeOfDayView != null ? s.purgeOldLogActionsTimeOfDayView : '00:00';
    s.purgeOldMessagesTimeOfDay = s.purgeOldMessagesTimeOfDayView != null ? s.purgeOldMessagesTimeOfDayView : '00:00';
    s.purgeOldAdItemsTimeOfDay = s.purgeOldAdItemsTimeOfDayView != null ? s.purgeOldAdItemsTimeOfDayView : '00:00';
    s.purgeOldStoreItemPriceChangesTimeOfDay = s.purgeOldStoreItemPriceChangesTimeOfDayView != null ? s.purgeOldStoreItemPriceChangesTimeOfDayView : '00:00';
    s.purgeOldStoreItemCostChangesTimeOfDay = s.purgeOldStoreItemCostChangesTimeOfDayView != null ? s.purgeOldStoreItemCostChangesTimeOfDayView : '00:00';
    s.purgeOldDenormalizedPreviewStoreItemPriceChangesTimeOfDay = s.purgeOldDenormalizedPreviewStoreItemPriceChangesTimeOfDayView != null ? s.purgeOldDenormalizedPreviewStoreItemPriceChangesTimeOfDayView : '00:00';

    // ensure minimal valid values
    if (s.logActionsTimeToLiveInDays == '' || s.logActionsTimeToLiveInDays < 1) s.logActionsTimeToLiveInDays = 1;

    return s;
  }

  // Internal deep equality moved into the class for encapsulation.
  private static deepEqual(a: unknown, b: unknown): boolean {
    if (Object.is(a, b)) return true;
    if (typeof a !== typeof b) return false;

    if (a && b && typeof a === 'object') {
      if (Array.isArray(a)) {
        return Array.isArray(b)
          && a.length === (b as unknown[]).length
          && a.every((v, i) => AdminScheduleComponent.deepEqual(v, (b as unknown[])[i]));
      }
      const ak = Object.keys(a as object);
      const bk = Object.keys(b as object);
      if (ak.length !== bk.length) return false;
      return ak.every(k => AdminScheduleComponent.deepEqual((a as JSONish)[k], (b as JSONish)[k]));
    }
    return false;
  }

  // Batch window state refresh: tries one server call, falls back to current one-by-one calls if missing
  private applyWindowOpenStates(states: Record<string, boolean>): void {
    if (!states) return;

    // Directly assign to known properties to ensure Angular change detection works
    if (states.hasOwnProperty('serverMaintenanceWindow')) {
      this.isServerMaintenanceWindowOpen = states['serverMaintenanceWindow'];
    }
    if (states.hasOwnProperty('previewPricingWindow')) {
      this.isPreviewPricingWindowOpen = states['previewPricingWindow'];
    }
    if (states.hasOwnProperty('meatPreviewPricingWindow')) {
      this.isMeatPreviewPricingWindowOpen = states['meatPreviewPricingWindow'];
    }
    if (states.hasOwnProperty('producePreviewPricingWindow')) {
      this.isProducePreviewPricingWindowOpen = states['producePreviewPricingWindow'];
    }
    if (states.hasOwnProperty('storeItemPriceChangesWindow')) {
      this.isStoreItemPriceChangesWindowOpen = states['storeItemPriceChangesWindow'];
    }
    if (states.hasOwnProperty('meatStoreItemPriceChangesWindow')) {
      this.isMeatStoreItemPriceChangesWindowOpen = states['meatStoreItemPriceChangesWindow'];
    }
    if (states.hasOwnProperty('produceStoreItemPriceChangesWindow')) {
      this.isProduceStoreItemPriceChangesWindowOpen = states['produceStoreItemPriceChangesWindow'];
    }
    
    // Force Angular to detect the changes
    this.cdr.detectChanges();
  }

  private refreshWindowOpenStatesBatch(): void {
    // Check if the service method exists before calling
    if (typeof this._scheduleService.getScheduleWindowOpenStates === 'function') {
      this._scheduleService.getScheduleWindowOpenStates()
        .subscribe(
          (states: Record<string, boolean>) => {
            this.applyWindowOpenStates(states);
          },
          _ => this.refreshWindowOpenStatesFallback()
        );
    } else {
      // Method doesn't exist, use fallback
      this.refreshWindowOpenStatesFallback();
    }
  }

  private refreshWindowOpenStatesFallback(): void {
    const windows = [
      'ServerMaintenanceWindow',
      'PreviewPricingWindow',
      'MeatPreviewPricingWindow',
      'ProducePreviewPricingWindow',
      'StoreItemPriceChangesWindow',
      'MeatStoreItemPriceChangesWindow',
      'ProduceStoreItemPriceChangesWindow'
    ];
    for (const w of windows) this.isScheduleWindowOpen(w);
  }

  // Scroll to the first card that has validation errors
  private scrollToFirstErrorCard(): void {
    // Use setTimeout to allow DOM to update with error classes first
    setTimeout(() => {
      // First, try to find card by mapped element ID
      const errorGroups = Object.keys(this.errors).filter(g => !!this.errors[g]);
      
      for (const group of errorGroups) {
        const elementId = this.errorGroupToElementId[group];
        if (elementId) {
          const element = document.getElementById(elementId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            console.log(`[AdminSchedule] Scrolled to error card: ${elementId}`);
            return;
          }
        }
      }

      // Fallback: find first element with error-highlight class
      const errorElement = document.querySelector('.error-highlight');
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        console.log('[AdminSchedule] Scrolled to first error-highlight element');
        return;
      }

      // Second fallback: find first card-error div with content
      const cardErrors = document.querySelectorAll('.card-error');
      for (const cardError of Array.from(cardErrors)) {
        if (cardError.textContent?.trim()) {
          cardError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          console.log('[AdminSchedule] Scrolled to first card-error with content');
          return;
        }
      }

      console.log('[AdminSchedule] No error element found to scroll to');
    }, 100);
  }
}
